import {
  useGetCredit,
  useGetDailyCreditUsage,
  useGetCreditBalanceSummaryOption,
} from '@/service/payment/hook';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import { Coins, ExternalLink, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addDailyCreditUsage } from '@/service/payment';
import { fFullDateTime } from '@/utils/formatTime';
import { Link } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import siteConfig from '@/config';
import { Skeleton } from '@/components/ui/skeleton';

type Props = {
  isShowPaymentLink?: boolean;
};

const ViewCredit = ({ isShowPaymentLink }: Props) => {
  const {
    data: dailyCreditUsage,
    refetch: refetchDailyCreditUsage,
    isLoading: isLoadingDailyCreditUsage,
  } = useGetDailyCreditUsage();

  const {
    data,
    refetch: refetchCredit,
    isLoading: isLoadingCredit,
    isSuccess,
  } = useQuery({
    ...useGetCreditBalanceSummaryOption(),
  });
  const totalAvailableCredit = data?.balances?.[0]?.available_balance?.monetary?.value
    ? Number(data.balances[0].available_balance.monetary.value) / 100
    : 0;

  const currentUsage = dailyCreditUsage?.current_usage || 0;
  // const isOverLimit = currentUsage > totalAvailableCredit;
  // const remainingCredit = totalAvailableCredit - currentUsage;

  const mutateAddDailyCreditUsage = useMutation({
    mutationFn: async () => {
      const res = await addDailyCreditUsage();
      return res;
    },
    onSuccess: () => {
      refetchDailyCreditUsage();
      refetchCredit();
      toast.success('Claim daily free credit successfully');
    },
    onError: () => {
      toast.error('Claim daily free credit failed');
    },
  });

  const handleClaimDailyFreeCredit = async () => {
    console.log('claim daily free credit');
    mutateAddDailyCreditUsage.mutate();
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="h-4 w-4" />
            Credit Usage
          </CardTitle>
          {isShowPaymentLink && (
            <Link
              to={PATH_PAGE.setting.payment}
              target="_blank"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
            >
              Go to payment <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div>
            <span className="text-muted-foreground">Used today</span>
            <p className="text-xs text-muted-foreground">
              {`(${fFullDateTime(dayjs().utc().startOf('day').toISOString())} - ${fFullDateTime(dayjs().utc().add(1, 'day').startOf('day').toISOString())} UTC)`}
            </p>
          </div>
          <span className={cn('font-medium')}>
            {isLoadingDailyCreditUsage ? (
              <Skeleton className="h-4 w-10" />
            ) : (
              fCurrencyUSDExtended(currentUsage)
            )}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Credit grant</span>
          <span className="font-medium text-green-600">
            {isLoadingCredit ? (
              <Skeleton className="h-4 w-10" />
            ) : (
              fCurrencyUSDExtended(totalAvailableCredit || 0)
            )}
          </span>
        </div>

        {siteConfig.other.claimDailyFreeCredit &&
          isSuccess &&
          totalAvailableCredit <= 0 && (
            <>
              <Separator />
              <Button
                variant={'primary'}
                className="w-full"
                onClick={handleClaimDailyFreeCredit}
                disabled={mutateAddDailyCreditUsage.isPending}
              >
                🎁 Claim Daily Free Credit
              </Button>
            </>
          )}
      </CardContent>
    </Card>
  );
};

export default ViewCredit;
